import {
  BackendApiError,
  batchCreateGroups,
  createGroup,
  deleteGroup,
  deleteStudent,
  type GroupType,
  updateGroup,
} from '@lms/api-client/backend';

// Ports features/groups/actions.ts. Server actions wired to Next forms become
// plain async functions that call the backend and return a result the caller
// acts on. Revalidation (`revalidatePath`) is handled by the caller via SWR
// `mutate`, and the redirect after delete becomes a `useNavigate` call.

function toGroupType(value: string | undefined): GroupType {
  return value === 'supplier' ? 'supplier' : 'tenant';
}

export interface AddGroupInput {
  name: string;
  groupType?: string;
  locationId: number;
}

export type AddGroupResult = { ok: true } | { ok: false; message: string };

export async function addGroup(input: AddGroupInput): Promise<AddGroupResult> {
  try {
    const { name, groupType, locationId } = input;

    await createGroup({
      name,
      locationId,
      groupType: toGroupType(groupType),
    });

    return { ok: true };
  } catch (e) {
    if (e instanceof BackendApiError && e.status === 409) {
      return { ok: false, message: 'Duplicate group in list!' };
    }
    console.error(e);
    return { ok: false, message: `${e}` };
  }
}

export interface BatchAddGroupsInput {
  names: string;
  grouptype: string;
  locationId: number;
}

export type BatchAddGroupsResult =
  | { ok: true }
  | { ok: false; message: string };

export async function batchAddGroups(
  input: BatchAddGroupsInput,
): Promise<BatchAddGroupsResult> {
  try {
    const { names, grouptype, locationId } = input;

    const groupsToCreate = names
      .split('\n')
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name) => ({
        name,
        locationId,
        groupType: toGroupType(grouptype),
      }));

    if (groupsToCreate.length > 0) {
      await batchCreateGroups(groupsToCreate);
    }

    return { ok: true };
  } catch (e) {
    console.error(e);
    return { ok: false, message: `${e}` };
  }
}

export async function removeGroup(id: number): Promise<void> {
  await deleteGroup(id);
}

export interface SaveBasicDataInput {
  group: number;
  image: string;
  name: string;
  unitId: string;
  groupType: GroupType;
}

export type SaveBasicDataResult =
  | { error: ''; success: true }
  | { error: string; success: false };

// Ports `saveBasicData`. The backend `updateGroup` accepts only `name`/`logo`,
// so the group type shown in the form is not persisted here (kept for parity
// with the source UI).
export async function saveBasicData(
  input: SaveBasicDataInput,
): Promise<SaveBasicDataResult> {
  try {
    const { group, image, name, unitId } = input;

    const upd: { logo?: string; name?: string; unitId?: string } = {
      name,
      unitId,
    };
    if (image) {
      upd.logo = image;
    }

    await updateGroup(group, upd);

    return { error: '', success: true };
  } catch (e) {
    return { error: `${e}`, success: false };
  }
}

export async function removeStudent(id: number): Promise<void> {
  await deleteStudent(id);
}
